import React, { useCallback, useMemo } from 'react';
import { IDevice } from '@pages/Assets/Inventory/types';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import useSwr from '@hooks/useSwr';
import { Skeleton } from 'antd';
import ActionTypes from '@utils/deviceActions';
import { IDeviceActionRequestPayload } from '../types';
import useUpload from './Upload';

interface IActionNetworkReset {
  devicesProps: IDevice[] | undefined;
  uploadedDevices: any;
  actionKey: number;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  setUploadedDevices: any;
}

const useHlrReprog = ({
  actionKey,
  devicesProps,
  uploadedDevices,
  handleDeviceActions,
  setUploadedDevices,
}: IActionNetworkReset) => {
  const { t } = useTranslation('device-actions-modal');

  const { data: numMaxData } = useSwr<{
    subscriberReprogrammingFileLength: number;
  }>(
    actionKey === ActionTypes.HLR_REPROG
      ? '/service-proxy/configurations/subscriber'
      : null,
  );

  const maxNumOfDevices = numMaxData?.subscriberReprogrammingFileLength ?? 0;

  const devices = useMemo(
    () => (uploadedDevices?.length > 0 ? uploadedDevices : devicesProps),
    [devicesProps, uploadedDevices],
  );

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = actionKey;

    payload.devices =
      devicesProps && devicesProps.length > 0
        ? devicesProps?.map(device => ({
            msisdn: device.msisdn,
          }))
        : uploadedDevices;
    return handleDeviceActions(payload);
  }, [actionKey, devicesProps, uploadedDevices, handleDeviceActions]);

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey,
    setUploadedDevices,
  });

  const renderNumOfDevices = useMemo(
    () => (
      <Skeleton
        loading={!numMaxData}
        paragraph={false}
        active
        style={{ marginTop: 24 }}
      >
        <p
          style={{
            opacity: 0.8,
            textAlign: 'center',
            marginTop: 16,
            fontSize: '0.8rem',
            color:
              devices?.length > maxNumOfDevices
                ? 'red'
                : 'var(--primary-color)',
          }}
        >
          <span style={{ marginRight: 8, fontSize: '0.9rem' }}>
            <InfoCircleOutlined />
          </span>
          {`${t('max-num-of-devices')}: `}

          <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
            {t('num-of-devices', { count: maxNumOfDevices })}
          </span>
        </p>
      </Skeleton>
    ),
    [devices?.length, maxNumOfDevices, numMaxData, t],
  );

  const uploadStepExtra = useMemo(
    () => ({
      ...uploadStep,
      content: (
        <>
          {uploadStep?.content}
          {renderNumOfDevices}
        </>
      ),
    }),
    [renderNumOfDevices, uploadStep],
  );

  const contentSteps = useMemo(() => {
    const selectedMoreThenTenStep = {
      title: 'Informação',
      content: renderNumOfDevices,
    };
    if (devicesProps && devicesProps.length > maxNumOfDevices) {
      return [selectedMoreThenTenStep];
    }
    return !devicesProps || devicesProps?.length === 0 ? [uploadStepExtra] : [];
  }, [renderNumOfDevices, devicesProps, maxNumOfDevices, uploadStepExtra]);

  const nextStepIsDisabled = useCallback(
    () => !devices || devices.length === 0 || devices.length > maxNumOfDevices,
    [devices, maxNumOfDevices],
  );

  const reinitState = () => {
    uploadReinitState();
    setUploadedDevices([]);
  };

  const hlrReprogData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { hlrReprogData };
};

export default useHlrReprog;
